import React from 'react';
import SidebarParametres from '../../../layout/SidebarParametres';
import { useOAD } from '../../../layout/OADLayout';
import { api } from '../../shared/api/api';
import { useApiData } from '../../shared/hooks/useFetch';
import { RCP_OPTIONS, RCP_DEFAUT } from '../../shared/constants';
import { horizonsSelectionnables } from '../../shared/utils/horizon';
import {
  normaliserImpacts,
  horizonsGraphique,
  horizonReference,
  libelleHorizon,
  moyennePourHorizon,
} from '../../shared/utils/normaliserImpacts';
import {
  aleasParCulturePourAnnee,
  repartitionCulturesPourAnnee,
  totalEtRepartitionAleas,
} from '../../shared/utils/pertes';
import RisqueMoyenChart from '../../shared/charts/RisqueMoyenChart';
import RisquesNiveauxChart from '../../shared/charts/RisquesNiveauxChart';
import PertesChart from '../../shared/charts/PertesChart';
import RepartitionCulturesChart from '../../shared/charts/RepartitionCulturesChart';
import CarteRisquePRA from '../../shared/charts/CarteRisquePRA';
import AleasParCultureChart from '../../shared/charts/AleasParCultureChart';
import LimiteErreur from '../../shared/components/LimiteErreur';

/**
 * La sidebar raisonne en scénario RCP, l'API nomme ses champs en degrés de
 * réchauffement. La correspondance est explicite ici plutôt que dispersée dans
 * le code, pour qu'elle soit facile à corriger si elle évolue.
 */
const SCENARIO_PAR_RCP = {
  '4.5': '2degre',
  '8.5': '4degre',
  hybride: 'hybride',
  tracc: 'tracc',
};

// Les 4 premiers éléments d'un tableau R (le 5e existe mais n'est pas affiché).
const NIVEAUX = [
  { index: 0, label: 'Très courant', periode: '3 ans', couleur: '#7030A2' },
  { index: 1, label: 'Courant', periode: '7 ans', couleur: '#FABE06' },
  { index: 2, label: 'Exceptionnel', periode: '20 ans', couleur: '#0027D6' },
  { index: 3, label: 'Catastrophique', periode: '50 ans', couleur: '#DB04B9' },
];

/**
 * Groupe tel qu'écrit par la route "pertes" (YearGroup de repartitionAleas)
 * pour un horizon interne : 2035 pour les RCP, "+2"/"reference" en TRACC.
 */
function groupePertes(scenario, horizon) {
  if (scenario !== 'tracc') return Number(horizon);
  return horizon === 'reference' ? 'reference' : `+${horizon}`;
}

/** Tableau d'évolution : une ligne par horizon, une colonne par niveau. */
function TableauEvolution({
  titre, scenario, risques, moyennes, horizons, reference,
  produitBrut, format, commune, pra, absolu = false,
}) {
  const valeursReference = risques?.[reference];
  if (!valeursReference) return null;

  const moyenneReference = moyennePourHorizon(moyennes, scenario, reference);

  const formater = (valeur) =>
    format === 'euros'
      ? `${Number(Math.round((valeur * produitBrut) / 100)).toLocaleString()} €`
      : `${valeur.toFixed(1)} pts`;

  // En valeur absolue (tableau euros) : on ajoute la référence en tête, pour
  // avoir 2020 au-dessus de 2035. En différentiel (tableau points) : inchangé,
  // seuls les horizons sélectionnables, pas de ligne de référence.
  const lignesHorizons = absolu ? [reference, ...horizons] : horizons;

  return (
    // lang="fr" : indispensable pour que la césure CSS (hyphens: auto) des
    // en-têtes se fasse selon les règles du français.
    <div className="ig-tableau" lang="fr">
      <div className="ig-tableau-titre">{titre}</div>
      {commune && (
        <div className="ig-tableau-sous-titre" style={{textAlign: 'center'}}>
          Commune : <b>{commune}</b>
        </div>
      )}
      {pra && (
        <div className="ig-tableau-sous-titre" style={{textAlign: 'center'}}>
          Petite région agricole : <b>{pra}</b>
        </div>
      )}

      <div className="ig-ligne ig-entete">
        <div className="ig-cellule ig-cellule-horizon">
          {absolu
            ? (scenario === 'tracc' ? 'Réchauffement' : 'Année')
            : `Par rapport à ${libelleHorizon(scenario, reference)}`}
        </div>
        {NIVEAUX.map((niveau) => (
          <div
            key={niveau.index}
            className="ig-cellule ig-cellule-entete"
            style={{ backgroundColor: niveau.couleur }}
          >
            {niveau.label}
            <br />({niveau.periode})
          </div>
        ))}
        <div className="ig-cellule ig-cellule-entete ig-cellule-moyen">Moyen</div>
      </div>

      {lignesHorizons.map((horizon) => {
        const valeurs = risques[horizon];
        if (!valeurs) return null;
        const moyenne = moyennePourHorizon(moyennes, scenario, horizon);

        return (
          <div key={horizon} className="ig-ligne">
            <div className="ig-cellule ig-cellule-horizon">
              {libelleHorizon(scenario, horizon)}
            </div>
            {NIVEAUX.map((niveau) => {
              const valeurBrute = valeurs[niveau.index];
              const valeurAffichee = absolu
                ? valeurBrute
                : valeurBrute - valeursReference[niveau.index];
              return (
                <div key={niveau.index} className="ig-cellule">
                  {formater(valeurAffichee)}
                </div>
              );
            })}
            <div className="ig-cellule ig-cellule-moyen">
              {moyenne !== undefined && (absolu || moyenneReference !== undefined)
                ? formater(absolu ? moyenne : moyenne - moyenneReference)
                : '—'}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** Les 4 encarts "risque_value" : niveau de risque et valeur colorée. */
function RisquesValue({ valeurs, moyenne }) {
  if (!valeurs) return null;

  const encarts = [
    { label: 'Moyen', valeur: moyenne, couleur: '#6FFF6F' },
    ...NIVEAUX.slice(1).map((n) => ({
      label: `${n.label} (${n.periode})`,
      valeur: valeurs[n.index],
      couleur: n.couleur,
    })),
  ];

  return (
    <div className="ig-risques-value">
      {encarts.map((encart) => (
        <div key={encart.label} className="ig-risque-value">
          <div className="ig-risque-value-label">{encart.label}</div>
          <div className="ig-risque-value-nombre" style={{ color: encart.couleur }}>
            {encart.valeur !== undefined ? `${Number(encart.valeur).toFixed(1)} %` : '—'}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function ImpactsGlobaux() {
  const { idExploit } = useOAD();

  const [rcp, setRcp] = React.useState(RCP_DEFAUT);
  const scenario = SCENARIO_PAR_RCP[rcp];

  // Plus de choix d'année dans la sidebar : toute la page affiche les trois
  // horizons du scénario (2020/2035/2050, ou Référence/+2/+2.7 en TRACC).
  const horizons = horizonsGraphique(scenario);
  const horizonsPossibles = horizonsSelectionnables(scenario);
  const reference = horizonReference(scenario);
  // "2020, 2035 et 2050" / "Référence, +2 °C et +2.7 °C" pour les titres.
  const libellesHorizons = horizons.map((h) => libelleHorizon(scenario, h));
  const libelleHorizons = `${libellesHorizons.slice(0, -1).join(', ')} et ${libellesHorizons.at(-1)}`;

  // La route "pertes" et la carte attendent toujours un paramètre `annee`.
  // "pertes" renvoie de toute façon les trois horizons dans sa réponse ; la
  // carte, elle, n'affiche qu'un horizon : on garde celui qui était affiché
  // par défaut avant (2035 / +2 °C).
  const anneeRequete = horizonsPossibles[0];

  const parametres = React.useMemo(
    () => ({ idExploitation: idExploit }),
    [idExploit]
  );
  const parametresAnnee = React.useMemo(
    () => ({ idExploitation: idExploit, annee: anneeRequete, rcp }),
    [idExploit, anneeRequete, rcp]
  );

  const { data, loading, error } = useApiData(api.getImpactsGlobaux, parametres);
  // Une seule route "pertes" alimente les 3 blocs du bas (camemberts des
  // aléas, répartition par culture, pertes par culture/aléa), cf.
  // shared/utils/pertes.js.
  const { data: donneesPertes } = useApiData(api.getPertes, parametresAnnee);
  const { data: carte } = useApiData(api.getCarteRisquePRA, parametresAnnee);

  const { risques, moyennes } = React.useMemo(() => normaliserImpacts(data), [data]);

  const produitBrut = data?.PBTot ?? 0;
  const scenarioIndisponible = !loading && !error && !risques[scenario];
  // On n'affiche le contenu que si le scénario et sa référence sont
  // réellement présents dans la réponse : un scénario partiellement renvoyé
  // ne doit pas casser la page.
  const pret = !loading && !error && risques[scenario]?.[reference];

  const libelleGroupe = (groupe) => {
    const horizon = horizons.find((h) => String(groupePertes(scenario, h)) === String(groupe));
    return horizon !== undefined ? libelleHorizon(scenario, horizon) : String(groupe);
  };

  // repartitionAleas ne contient pas de "total" par année (juste le détail
  // par aléa) : on le recalcule en sommant les 5 aléas de chaque année, puis
  // on ne garde que les trois horizons tracés.
  const pertesHorizons = React.useMemo(() => {
    const { total, repartition } = totalEtRepartitionAleas(donneesPertes);
    const groupes = horizonsGraphique(scenario).map((h) => String(groupePertes(scenario, h)));
    const garde = (d) => groupes.includes(String(d.YearGroup));

    const totalFiltre = total.filter(garde);
    if (totalFiltre.length === 0) return null;
    return { total: totalFiltre, repartition: repartition.filter(garde) };
  }, [donneesPertes, scenario]);

  // Répartition par culture et pertes par culture/aléa : un tableau par
  // horizon dans la réponse, on les prend tous les trois.
  const { repartitionCulturesHorizons, aleasParCultureHorizons } = React.useMemo(() => {
    const parHorizon = (extraire) =>
      horizonsGraphique(scenario).map((h) => ({
        cle: String(h),
        label: libelleHorizon(scenario, h),
        data: extraire(donneesPertes, rcp, h),
      }));
    return {
      repartitionCulturesHorizons: parHorizon(repartitionCulturesPourAnnee),
      aleasParCultureHorizons: parHorizon(aleasParCulturePourAnnee),
    };
  }, [donneesPertes, rcp, scenario]);

  return (
    <div className="oad-contenu">
      <SidebarParametres
        niveau1="2035-2050"
        niveau2="impacts-globaux"
        niveau3={null}
        idExploit={idExploit}
      >
        <fieldset className="sidebar-champ sidebar-groupe">
          <legend className="sidebar-label">Scénarios climatiques</legend>
          {RCP_OPTIONS.map((option) => (
            <label key={option.value} className="sidebar-radio">
              <input
                type="radio"
                name="scenario-clim-impacts"
                value={option.value}
                checked={rcp === option.value}
                onChange={(e) => setRcp(e.target.value)}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </fieldset>
      </SidebarParametres>

      <main className="oad-cards">
        {/* Card en tête : les 4 encarts de risque pour 2020/référence, le
            risque "actuel". */}
        {pret && (
          <section className="oad-card">
            <h2 className="tdb-section-titre">Pertes actuelles</h2>
            <RisquesValue
              valeurs={risques[scenario][reference]}
              moyenne={moyennePourHorizon(moyennes, scenario, reference)}
            />
          </section>
        )}

        {/* Card 1 : graphiques à gauche, tableaux à droite */}
        <section className="oad-card">
          <h2 className="tdb-section-titre">
            Comparaison des pertes actuelles et futures sur votre exploitation
          </h2>
          {error && <p className="tdb-erreur">{error}</p>}
          {loading && <p className="tdb-chargement">Chargement…</p>}
          {scenarioIndisponible && (
            <p className="tdb-chargement">
              Ce scénario n'est pas encore renvoyé par l'API.
            </p>
          )}

          {pret && (
            <div className="ig-card1">
              <div className="ig-colonne-graphiques">
                <LimiteErreur cle={`niveaux-${scenario}`}>
                  {/* Les trois horizons, chacun dans sa couleur YEAR_COLORS. */}
                  <RisquesNiveauxChart
                    scenario={scenario}
                    risques={risques[scenario]}
                    horizons={horizons}
                  />
                </LimiteErreur>
              </div>

              <div className="ig-colonne-tableaux">
                <TableauEvolution
                  titre="Evolution de la perte en % de produit brut en point"
                  scenario={scenario}
                  risques={risques[scenario]}
                  moyennes={moyennes}
                  horizons={horizonsPossibles}
                  reference={reference}
                  produitBrut={produitBrut}
                  format="points"
                  commune={data?.commune}
                  pra={data?.libellePRA}
                />
                <TableauEvolution
                  titre="Montant des pertes en euros"
                  scenario={scenario}
                  risques={risques[scenario]}
                  moyennes={moyennes}
                  horizons={horizonsPossibles}
                  reference={reference}
                  produitBrut={produitBrut}
                  format="euros"
                  // En valeur absolue : ligne 2020/référence ajoutée
                  // au-dessus de 2035, plus de différence par rapport à 2020.
                  absolu
                />
              </div>
            </div>
          )}

          {/* Le risque moyen occupe toute la largeur, sous les deux colonnes. */}
          {pret && (
            <div className="ig-risque-moyen">
              <h3 className="ig-camembert-titre">
                Perte moyenne {libelleHorizons}
              </h3>
              <LimiteErreur cle={scenario}>
                <RisqueMoyenChart
                  scenario={scenario}
                  moyennes={moyennes}
                  horizons={horizons}
                />
              </LimiteErreur>
            </div>
          )}
        </section>

        {/* Card 2 : camemberts (une ligne aléas, une ligne cultures) et
            pertes par culture, pour les trois horizons. */}
        {pret && (
          <section className="oad-card">
            <h2 className="tdb-section-titre">
              Pertes {libelleHorizons}
            </h2>

            <div className="ig-camemberts" style={{ flexDirection: 'column' }}>
              <div className="ig-camembert">
                <h3 className="ig-camembert-titre">Répartition des aléas climatiques pour la perte moyenne</h3>
                {pertesHorizons ? (
                  <LimiteErreur cle={`pertes-${scenario}`}>
                    <PertesChart
                      data={pertesHorizons}
                      risque="courant"
                      rcp={rcp}
                      afficherCourbe={false}
                      libelleAnnee={libelleGroupe}
                    />
                  </LimiteErreur>
                ) : (
                  <p className="tdb-chargement">Chargement…</p>
                )}
              </div>

              <div className="ig-camembert">
                <h3 className="ig-camembert-titre">Répartition des cultures pour la perte moyenne</h3>
                <LimiteErreur cle={`repartition-${scenario}`}>
                  <RepartitionCulturesChart annees={repartitionCulturesHorizons} />
                </LimiteErreur>
              </div>
            </div>

            <div className="ig-aleas-culture">
              <h3 className="ig-camembert-titre">Pertes par culture et par aléa pour la perte moyenne</h3>
              <LimiteErreur cle={`aleas-${scenario}`}>
                <AleasParCultureChart annees={aleasParCultureHorizons} />
              </LimiteErreur>
            </div>
          </section>
        )}

        {/* Card 3 : carte du risque moyen de la PRA et de ses voisines */}
        {pret && (
          <section className="oad-card">
            <h2 className="tdb-section-titre">
              Perte climatique moyenne de votre petite région agricole et des PRA voisines
            </h2>
            <LimiteErreur cle={`carte-${scenario}`}>
              <CarteRisquePRA
                data={carte?.data}
                champ="risqueMoyen"
                idPraExploitation={carte?.idPraExploitation}
              />
            </LimiteErreur>
          </section>
        )}
      </main>
    </div>
  );
}
